export const SOCIAL_PLATFORMS = [
  'instagram',
  'tiktok',
  'facebook',
  'x',
  'linkedin',
  'website'
] as const;

export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];
export type SocialLinks = Partial<Record<SocialPlatform, string>>;

export function parseSocialLinks(raw: string | null | undefined): SocialLinks {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const links: SocialLinks = {};
    for (const platform of SOCIAL_PLATFORMS) {
      const value = (parsed as Record<string, unknown>)[platform];
      if (typeof value === 'string' && value.trim()) {
        links[platform] = value.trim();
      }
    }
    return links;
  } catch {
    return {};
  }
}

/**
 * Accepts a full http(s) URL or a bare host/path (`fb.com/oleg`, `oleg.com`)
 * and returns an https URL. Leaves other schemes (`javascript:`) untouched so
 * validation can reject them.
 */
function hasExplicitScheme(value: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(value);
}

export function normalizeOptionalHttpUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  if (hasExplicitScheme(trimmed) && !/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  const candidate = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed.replace(/^\/+/, '')}`;

  try {
    const url = new URL(candidate);
    if (!url.hostname) return '';
    return candidate;
  } catch {
    return '';
  }
}

export function emptySocialLinks(): Record<SocialPlatform, string> {
  return {
    instagram: '',
    tiktok: '',
    facebook: '',
    x: '',
    linkedin: '',
    website: ''
  };
}

export const MAX_SOCIAL_LINK_LENGTH = 300;

const SOCIAL_LINK_PATTERN = /^https?:\/\/\S+$/i;

export function isAcceptableSocialLink(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed && /\s/.test(trimmed)) return false;
  const normalized = normalizeOptionalHttpUrl(value);
  return (
    normalized === '' ||
    (normalized.length <= MAX_SOCIAL_LINK_LENGTH &&
      SOCIAL_LINK_PATTERN.test(normalized))
  );
}
